import { defineStore } from 'pinia'
import ContractFilesService from '@/api/ContractFilesService.js'
import { useContractStore } from './contract'

export const useContractFilesStore = defineStore('contractFiles', {
    state: () => ({
        publicTypes: [
            'equipment_purchase_contract',
            'leasing_application_signed',
            'handover_protocol',
            'takeover_protocol',
            'machine_photo',
            'maintenance_contract',
        ],

        internalTypes: ['dnb_report', 'final_pricing_file', 'transaction_memo'],

        insuranceTypes: [
            'insurance_policy_general_insurance',
            'insurance_certificate_general_insurance',
            'insurance_policy_machine_breakdown',
            'insurance_certificate_machine_breakdown',
        ],

        optionalTypes: [
            'dnb_report_guarantor',
            'inter_company_guarantee',
            'historical_usage_data',
            'lessee_purchase_order_for_leasing_contract',
            'certificated_land_charge',
        ],

        publicFiles: {},
        internalFiles: {},
        // The compliance reports of the contract, as returned by contract.files.compliance.
        complianceReports: [],
        // The same files keyed by compliance report id, so the shared file components (and the
        // delete action) can address them the way they address a file type in the other sections.
        complianceFiles: {},
        insuranceFiles: {},
        maintenanceProtocolFiles: {},
        maintenanceProtocolPeriods: {},

        optionalFilesTableRefreshKey: 0,
        missingContractDocumentsTableRefreshKey: 0,
        downloading: false,
        loading: false,
        deleting: false,
    }),

    getters: {},

    actions: {
        _contractStore() {
            return useContractStore()
        },

        _createFileState(fileTypes) {
            return Object.fromEntries(fileTypes.map((type) => [type, []]))
        },

        _initializeStore() {
            this.publicFiles = this._createFileState(this.publicTypes)
            this.internalFiles = this._createFileState(this.internalTypes)
            this.insuranceFiles = this._createFileState(this.insuranceTypes)
        },

        resetState() {
            this.publicFiles = {}
            this.internalFiles = {}
            this.complianceReports = []
            this.complianceFiles = {}
            this.insuranceFiles = {}
            this.maintenanceProtocolFiles = {}
            this.maintenanceProtocolPeriods = {}
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        addFileToPublicGroup(fileObject) {
            const group = this.insuranceTypes.includes(fileObject.type)
                ? 'insuranceFiles'
                : 'publicFiles'
            this[group][fileObject.type] = [...this[group][fileObject.type], fileObject]
        },

        addFileToInternalGroup(fileObject) {
            this.internalFiles[fileObject.type] = [
                ...this.internalFiles[fileObject.type],
                fileObject,
            ]
        },

        addFileToMaintenanceProtocolGroup(fileObject) {
            if (!this.maintenanceProtocolFiles[fileObject.type]) {
                this.maintenanceProtocolFiles[fileObject.type] = []
            }
            this.maintenanceProtocolFiles[fileObject.type] = [
                ...this.maintenanceProtocolFiles[fileObject.type],
                fileObject,
            ]
        },

        async download(fileId, fileName) {
            try {
                await ContractFilesService.download(fileId, fileName)
            } catch (error) {
                console.error(
                    'Error downloading contract file (stores/contractFiles/download):',
                    error
                )
            }
        },

        async getPreviewUrl(fileId) {
            try {
                const { data } = await ContractFilesService.preview(fileId)
                return data.url
            } catch (error) {
                console.error(
                    'Error fetching preview URL (stores/contractFiles/getPreviewUrl):',
                    error
                )
                this.notification('error', '§ui_message.preview_failed')
                return null
            }
        },

        async fetchPublicFiles() {
            this.loading = true
            try {
                this.publicFiles = this._createFileState(this.publicTypes)
                this.insuranceFiles = this._createFileState(this.insuranceTypes)
                const contract = this._contractStore().contractId
                const { data } = await ContractFilesService.fetchFilesList({ contract }, 'public')
                data.public.forEach((file) => this.addFileToPublicGroup(file))
            } catch (error) {
                console.error(
                    'Error fetching public files (stores/contractFiles/fetchPublicFiles):',
                    error
                )
                return []
            } finally {
                this.loading = false
            }
        },

        async fetchInternalFiles() {
            this.loading = true
            try {
                this.internalFiles = this._createFileState(this.internalTypes)
                const contract = this._contractStore().contractId
                const { data } = await ContractFilesService.fetchFilesList({ contract }, 'internal')
                data.internal.forEach((file) => this.addFileToInternalGroup(file))
            } catch (error) {
                console.error(
                    'Error fetching internal files (stores/contractFiles/fetchInternalFiles):',
                    error
                )
                return []
            } finally {
                this.loading = false
            }
        },

        async fetchComplianceReportFiles() {
            this.loading = true
            try {
                const contract = this._contractStore().contractId
                const { data } = await ContractFilesService.fetchFilesList(
                    { contract },
                    'compliance'
                )
                this.complianceReports = data.compliance_reports
                this.complianceFiles = Object.fromEntries(
                    data.compliance_reports.map((report) => [report.id, report.files])
                )
            } catch (error) {
                console.error(
                    'Error fetching compliance report files (stores/contractFiles/fetchComplianceReportFiles):',
                    error
                )
                return []
            } finally {
                this.loading = false
            }
        },

        async fetchMaintenanceProtocolFiles() {
            this.loading = true
            try {
                const contract = this._contractStore().contractId
                const { data } = await ContractFilesService.fetchFilesList(
                    { contract },
                    'maintenance_protocols'
                )
                this.maintenanceProtocolFiles = this._createFileState(data.types)
                this.maintenanceProtocolPeriods = data.periods
                data.maintenance_protocols.forEach((file) =>
                    this.addFileToMaintenanceProtocolGroup(file)
                )
            } catch (error) {
                console.error(
                    'Error fetching maintenance protocol files (stores/contractFiles/fetchMaintenanceProtocolFiles):',
                    error
                )
                return []
            } finally {
                this.loading = false
            }
        },

        async delete({ file, group, type }) {
            this.deleting = true
            try {
                await ContractFilesService.delete(file)
                const isOptional = this.optionalTypes.includes(type)
                if (isOptional) {
                    this.optionalFilesTableRefreshKey += 1
                } else {
                    this[group][type] = this[group][type].filter((f) => f.id !== file)
                }
                this.notification('success', '§ui_message.delete_successful')
            } catch (error) {
                console.error('Error deleting contract file (stores/contractFiles/delete):', error)
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.deleting = false
            }
        },

        async updatePolicyId(fileId, policyId) {
            try {
                await ContractFilesService.updatePolicyId(fileId, policyId)

                const policyFileTypes = [
                    'insurance_policy_general_insurance',
                    'insurance_policy_machine_breakdown',
                ]

                policyFileTypes.forEach((fileType) => {
                    const files = this.insuranceFiles[fileType]
                    if (files) {
                        const file = files.find((f) => f.id === fileId)
                        if (file) file.policy_id = policyId
                    }
                })

                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error('Error updating policy ID:', error)
                this.notification('error', '§ui_message.update_failed')
                throw error
            }
        },
    },
})
